import React, { useContext, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Navbar,
  Nav,
  Container,
  NavDropdown,
  Offcanvas,
} from "react-bootstrap";
import {
  User,
  LayoutDashboard,
  Home,
  Activity,
  Tag,
  LogOut,
  Settings,
} from "lucide-react";
import { logout } from "../../services/api";
import PopupMessage from "../common/PopupMessage";
import { AuthContext } from "../../context/AuthContext";
import NotificationBell from "../common/NotificationBell";

function StudentNavbar() {
  const [expanded, setExpanded] = useState(false);
  const closeMenu = () => setExpanded(false);
  const navigate = useNavigate();
  const { setUser, user } = useContext(AuthContext);

  const [popup, setPopup] = useState({ show: false, type: "", message: "" });

  async function LogoutFun() {
    closeMenu();
    const response = await logout();
    if (response.status === 200) {
      setPopup({
        show: true,
        type: "success",
        message: response?.data?.message || "logged out successful!",
      });
      setTimeout(() => {
        setUser(null);
        navigate("/");
      }, 1500);
    } else {
      alert(`Error: ${response.data?.message}`);
    }
  }

  return (
    <>
      <Navbar
        bg="white"
        expand="lg"
        fixed="top"
        className="shadow-sm py-2"
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      >
        <Container fluid className="px-3 px-lg-4">
          {/* 1. Logo Section */}
          <Navbar.Brand
            as={NavLink}
            to="/home"
            className="d-flex align-items-center gap-2 me-auto"
          >
            <img
              src="/CampusPulsLogo.png"
              alt="Logo"
              style={{ height: "45px", width: "auto" }}
            />
            <div className="d-none d-sm-block lh-1">
              <h6 className="m-0 fw-bold text-primary">Student</h6>
              <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                Portal
              </small>
            </div>
          </Navbar.Brand>

          {/* 2. Mobile Actions */}
          <div className="d-flex align-items-center gap-2 d-lg-none">
            <div className="nav-link p-0">
              <NotificationBell />
            </div>
            <Navbar.Toggle
              aria-controls="offcanvasNavbar"
              className="border-0 p-1"
            />
          </div>

          {/* 3. Offcanvas Menu */}
          <Navbar.Offcanvas
            id="offcanvasNavbar"
            aria-labelledby="offcanvasNavbarLabel"
            placement="end"
            restoreFocus={false}
            show={expanded}
            onHide={closeMenu}
          >
            <Offcanvas.Header closeButton className="border-bottom pb-3">
              <Offcanvas.Title
                id="offcanvasNavbarLabel"
                className="fw-bold text-primary"
              >
                {user?.role || "Menu"}
              </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-3 p-lg-0">
              <Nav className="justify-content-center flex-grow-1 pe-3 gap-1 gap-lg-3">
                <StyledNavLink
                  to="/home"
                  icon={Home}
                  label="Home"
                  onClick={closeMenu}
                />
                <StyledNavLink
                  to="/activities/categories"
                  icon={Tag}
                  label="Categories"
                  onClick={closeMenu}
                />
                <StyledNavLink
                  to="/activities/my-activities"
                  icon={Activity}
                  label="My Activities"
                  onClick={closeMenu}
                />
                <StyledNavLink
                  to="/student-dashboard"
                  icon={LayoutDashboard}
                  label="Dashboard"
                  onClick={closeMenu}
                />
              </Nav>

              <div className="mt-auto mt-lg-0 border-top pt-3 border-lg-0 pt-lg-0 d-flex flex-column flex-lg-row align-items-lg-center gap-lg-3">
                <div className="d-none d-lg-block">
                  <NotificationBell />
                </div>

                {/* Desktop Dropdown */}
                <NavDropdown
                  className="d-none d-lg-block account-dropdown"
                  title={
                    <div className="d-flex align-items-center gap-2 border rounded-pill px-3 py-1 hover-shadow bg-light transition-all">
                      <User size={18} className="text-primary" />
                      <span className="small fw-semibold text-dark">
                        Account
                      </span>
                    </div>
                  }
                  align="end"
                >
                  <NavDropdown.Item as={NavLink} to="/user/profile">
                    Profile
                  </NavDropdown.Item>
                  <NavDropdown.Item as={NavLink} to="/user/settings">
                    Settings
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={LogoutFun} className="text-danger">
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>

                {/* Mobile Profile Links */}
                <div className="d-lg-none d-flex flex-column gap-1">
                  <p className="text-muted small fw-bold mb-2 ps-2 mt-2">
                    ACCOUNT
                  </p>
                  <StyledNavLink
                    to="/user/profile"
                    icon={User}
                    label="Profile"
                    onClick={closeMenu}
                    mobileOnly
                  />
                  <StyledNavLink
                    to="/user/settings"
                    icon={Settings}
                    label="Settings"
                    onClick={closeMenu}
                    mobileOnly
                  />
                  <div
                    onClick={LogoutFun}
                    className="custom-nav-link text-danger d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                  >
                    <LogOut size={18} className="me-3 icon-width" /> Logout
                  </div>
                </div>
              </div>
            </Offcanvas.Body>
          </Navbar.Offcanvas>
        </Container>
      </Navbar>

      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}

      {/* نفس الـ CSS بالظبط لتوحيد الشكل */}
      <style>{`
         .custom-nav-link {
            color: #495057;
            font-weight: 500;
            padding: 10px 15px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            text-decoration: none;
            transition: all 0.2s ease;
         }
         @media (min-width: 992px) {
            .custom-nav-link { padding: 0.5rem 0.8rem; margin: 0; }
            .icon-width { margin-right: 0.5rem !important; }
            .navbar-expand-lg .offcanvas {
                position: static; z-index: auto; flex-grow: 1; width: auto !important; height: auto !important;
                visibility: visible !important; background-color: transparent !important; border: 0 !important;
                transform: none !important; transition: none;
            }
            .navbar-expand-lg .offcanvas .offcanvas-header { display: none; }
            .navbar-expand-lg .offcanvas .offcanvas-body { display: flex; flex-grow: 0; padding: 0; overflow-y: visible; }
            .account-dropdown .dropdown-toggle::after { display: none !important; }
         }
         .custom-nav-link:hover { color: #0d6efd; background-color: rgba(13, 110, 253, 0.05); }
         .custom-nav-link.active { color: #0d6efd; background-color: rgba(13, 110, 253, 0.1); font-weight: 600; }
         .icon-width { min-width: 24px; margin-right: 1rem; }
         .hover-shadow:hover { box-shadow: 0 4px 6px rgba(0,0,0,0.1); background-color: white !important; }
         .transition-all { transition: all 0.3s ease; }
      `}</style>
    </>
  );
}

const StyledNavLink = ({ to, icon: Icon, label, onClick, mobileOnly }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={`custom-nav-link ${mobileOnly ? "" : ""}`}
  >
    <Icon size={18} className="icon-width" /> {label}
  </NavLink>
);

export default StudentNavbar;

// import React, { useContext, useState } from "react";
// import { NavLink, useNavigate } from "react-router-dom";
// import { Navbar, Nav, Container, NavDropdown } from "react-bootstrap"; // ❌ Removed Badge
// import { User, LayoutDashboard, Home, Activity, Tag } from "lucide-react"; // ❌ Removed Bell
// import { logout } from "../../services/api";
// import PopupMessage from "../common/PopupMessage";
// import { AuthContext } from "../../context/AuthContext";
// import NotificationBell from "../common/NotificationBell"; // ✅ Import Smart Bell

// function StudentNavbar() {
//   const [expanded, setExpanded] = useState(false);
//   const handleNavClick = () => setExpanded(false);
//   const navigate = useNavigate();
//   const { setUser } = useContext(AuthContext);

//   // ❌ Removed unreadCount state (handled internally by NotificationBell)
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });

//   async function LogoutFun() {
//     const response = await logout();
//     if (response.status === 200) {
//       setPopup({
//         show: true,
//         type: "success",
//         message: response?.data?.message || "logged out successful!",
//       });
//       setTimeout(() => {
//         setUser(null);
//         navigate("/");
//       }, 1500);
//     } else {
//       alert(`Error: ${response.data?.message}`);
//     }
//   }

//   const getNavLinkStyle = (isActive) => ({
//     color: isActive ? "#007bff" : "black",
//     fontWeight: isActive ? "600" : "normal",
//   });

//   return (
//     <>
//       <Navbar
//         bg="light"
//         expand="lg"
//         fixed="top"
//         className="shadow-sm px-4 py-2"
//         expanded={expanded}
//       >
//         <Container
//           fluid
//           className="d-flex justify-content-between align-items-center"
//         >
//           {/* Logo + Title */}
//           <div className="d-flex align-items-center">
//             <img
//               src="/CampusPulsLogo.png"
//               alt="CampusPulse Logo"
//               style={{ height: "55px", width: "auto", objectFit: "contain" }}
//               className="me-2"
//             />
//             <h4
//               style={{
//                 background: "linear-gradient(90deg, #007bff, #6610f2)",
//                 WebkitBackgroundClip: "text",
//                 WebkitTextFillColor: "transparent",
//                 fontWeight: "800",
//                 letterSpacing: "0.8px",
//                 fontSize: "15px",
//                 margin: 0,
//               }}
//             >
//               Student <LayoutDashboard size={20} />
//             </h4>
//           </div>

//           <Navbar.Toggle onClick={() => setExpanded(!expanded)} />
//         </Container>

//         <Navbar.Collapse id="student-navbar" className="px-4">
//           <Nav className="me-auto mt-2 mt-lg-0">
//             <Nav.Link
//               as={NavLink}
//               to="/home"
//               onClick={handleNavClick}
//               style={({ isActive }) => getNavLinkStyle(isActive)}
//               className="d-flex align-items-center me-3"
//             >
//               <Home size={18} className="me-1" /> Home
//             </Nav.Link>
//             <Nav.Link
//               as={NavLink}
//               to="/activities/categories"
//               onClick={handleNavClick}
//               style={({ isActive }) => getNavLinkStyle(isActive)}
//               className="d-flex align-items-center me-3"
//             >
//               <Tag size={18} className="me-1" /> Categories
//             </Nav.Link>
//             <Nav.Link
//               as={NavLink}
//               to="/activities/my-activities"
//               onClick={handleNavClick}
//               style={({ isActive }) => getNavLinkStyle(isActive)}
//               className="d-flex align-items-center me-3"
//             >
//               <Activity size={18} className="me-1" /> MyActivities
//             </Nav.Link>
//             <Nav.Link
//               as={NavLink}
//               to="/student-dashboard"
//               onClick={handleNavClick}
//               style={({ isActive }) => getNavLinkStyle(isActive)}
//               className="d-flex align-items-center"
//             >
//               <LayoutDashboard size={18} className="me-1" /> Dashboard
//             </Nav.Link>
//           </Nav>

//           <Nav className="ms-auto align-items-center position-relative">
//             {/* 🔥🔥 Smart Notification Bell 🔥🔥 */}
//             <div className="nav-link d-flex align-items-center me-2">
//               <NotificationBell />
//             </div>

//             {/* Profile Dropdown */}
//             <NavDropdown
//               title={<User size={22} />}
//               id="profile-dropdown"
//               align="end"
//               className="me-2"
//             >
//               <NavDropdown.Item
//                 as={NavLink}
//                 to="/user/profile"
//                 onClick={handleNavClick}
//               >
//                 Profile
//               </NavDropdown.Item>
//               <NavDropdown.Item
//                 as={NavLink}
//                 to="/user/settings"
//                 onClick={handleNavClick}
//               >
//                 Settings
//               </NavDropdown.Item>
//               <NavDropdown.Divider />
//               <NavDropdown.Item onClick={LogoutFun}>Logout</NavDropdown.Item>
//             </NavDropdown>
//           </Nav>
//         </Navbar.Collapse>
//       </Navbar>
//       {/* Popup Message */}
//       {popup.show && (
//         <PopupMessage
//           type={popup.type}
//           message={popup.message}
//           onClose={() => setPopup({ ...popup, show: false })}
//         />
//       )}
//     </>
//   );
// }

// export default StudentNavbar;
